import type {
  CenterOperator,
  CloseDiagnostic,
  OperationDetail,
  OperationEvent,
  OperationModuleCounters,
  OperationModuleRow,
  OperationsCenterSnapshot,
  OperationStats,
  OperationTask,
  OperationTracks,
} from '@groupe-reca/contracts'
import type { OperationsCenterRepository } from '../../application/OperationsCenterRepository'
import { OperationsCenterError } from '../../domain/operationsCenter'

// Démonstration du nouveau Centre des opérations — DONNÉES FICTIVES (noms, adresses, positions inventés), pour les
// tests, le développement sans base et les captures. Jamais utilisée en repli d'une vraie base.

const uuid = (prefix: number, n: number) =>
  `${String(prefix).padStart(8, '0')}-0000-4000-a000-${String(n).padStart(12, '0')}`
const P = (px: number, py: number) => ({ lat: 45.7981 - py * 0.00003, lng: -74.0369 + px * 0.000043 })

interface DemoRoute {
  id: string
  name: string
  color: string
  doneShare: number
  problemIndex: number | null
  problem?: { reason: 'ACCESS_BLOCKED' | 'VEHICLE_IN_DRIVEWAY'; note: string; minutesAgo: number }
  streets: [string, number, number, number, number, number, number, number][]
  startedMinutesAgo: number
}

const routes: DemoRoute[] = [
  {
    id: uuid(31, 1),
    name: 'Route 1 · Bourassa',
    color: '#63a7ff',
    doneShare: 0.62,
    problemIndex: 9,
    problem: { reason: 'ACCESS_BLOCKED', note: 'Banc de neige de la Ville devant l’entrée.', minutesAgo: 25 },
    startedMinutesAgo: 263,
    streets: [
      ['rue de St-Pierre', 470, 468, 600, 452, 8, 104, 4],
      ['rue Bel-Air', 515, 484, 585, 478, 5, 210, 4],
      ['rue Richard', 610, 486, 680, 480, 5, 88, 4],
      ['rue Paré', 495, 546, 548, 548, 4, 140, 2],
      ['rue Laflamme', 560, 556, 642, 553, 6, 301, 4],
      ['rue Bruno-Nantel', 560, 586, 665, 578, 7, 415, 4],
    ],
  },
  {
    id: uuid(31, 2),
    name: 'Route 2 · Rivière',
    color: '#a78bfa',
    doneShare: 0.48,
    problemIndex: 11,
    problem: {
      reason: 'VEHICLE_IN_DRIVEWAY',
      note: 'Véhicule gris stationné, aucune réponse à la porte.',
      minutesAgo: 45,
    },
    startedMinutesAgo: 256,
    streets: [
      ['rue Scott', 810, 300, 880, 297, 4, 612, 4],
      ['rue des Pins', 790, 222, 880, 218, 5, 70, 4],
      ['rue Aubry', 770, 200, 880, 195, 6, 53, 2],
      ['rue Giguère', 690, 188, 880, 172, 10, 120, 4],
      ['rue de Ste-Paule', 700, 140, 860, 130, 9, 220, 6],
    ],
  },
  {
    id: uuid(31, 3),
    name: 'Route 3 · Sud',
    color: '#f1a93a',
    doneShare: 0.36,
    problemIndex: 7,
    problem: { reason: 'ACCESS_BLOCKED', note: 'Clôture fermée à clé.', minutesAgo: 70 },
    startedMinutesAgo: 237,
    streets: [
      ['rue de Martigny O', 440, 632, 520, 628, 5, 410, 6],
      ['rue Rochon', 480, 680, 540, 677, 4, 29, 2],
      ['rue Marie-Victorin', 530, 635, 620, 628, 6, 180, 4],
      ['rue Marchand', 620, 660, 710, 650, 6, 95, 4],
      ['rue Castonguay', 640, 710, 750, 705, 7, 302, 4],
    ],
  },
  {
    id: uuid(31, 4),
    name: 'Route 4 · Nord',
    color: '#45c987',
    doneShare: 0.7,
    problemIndex: null,
    startedMinutesAgo: 261,
    streets: [
      ['104e Avenue', 590, 12, 680, 10, 6, 1040, 6],
      ['103e Avenue', 660, 40, 720, 38, 4, 1031, 4],
      ['102e Avenue', 590, 75, 650, 72, 4, 1022, 4],
      ['101e Avenue', 580, 95, 660, 90, 5, 1010, 4],
      ['rue Bélanger', 690, 112, 800, 108, 7, 512, 6],
    ],
  },
]

const clients = [
  'C. Tremblay',
  'M. Gagnon',
  'S. Côté',
  'J. Bouchard',
  'L. Gauthier',
  'A. Morin',
  'P. Lavoie',
  'N. Fortin',
]

interface DemoOperator {
  index: number
  name: string
  equipment: string
  routeIndex: number | null
  connection: 'online' | 'interrupted' | 'none'
  available: boolean
  meter: number
  noPosition?: boolean
}

const demoOperators: DemoOperator[] = [
  {
    index: 1,
    name: 'Marc Thibodeau',
    equipment: 'T-07 — Tracteur',
    routeIndex: 0,
    connection: 'online',
    available: false,
    meter: 1824.6,
  },
  {
    index: 2,
    name: 'Julie Paquette',
    equipment: 'T-03 — Tracteur',
    routeIndex: 1,
    connection: 'online',
    available: false,
    meter: 2311.2,
  },
  {
    index: 3,
    name: 'Steve Lachance',
    equipment: 'T-11 — Tracteur',
    routeIndex: 2,
    connection: 'interrupted',
    available: false,
    meter: 976.4,
  },
  {
    index: 4,
    name: 'Karine Ouellet',
    equipment: 'C-02 — Chargeuse',
    routeIndex: 3,
    connection: 'online',
    available: false,
    meter: 3402.9,
  },
  {
    index: 5,
    name: 'Patrick Roy',
    equipment: 'T-05 — Tracteur',
    routeIndex: null,
    connection: 'online',
    available: true,
    meter: 1540,
  },
  {
    index: 6,
    name: 'Éric Gagnon',
    equipment: 'T-09 — Tracteur',
    routeIndex: null,
    connection: 'online',
    available: false,
    meter: 688.1,
    noPosition: true,
  },
]

interface State {
  operationId: string
  version: number
  status: 'OPEN' | 'CLOSED' | 'CANCELLED'
  openedAt: string
  closedAt: string | null
  tasks: OperationTask[]
  events: OperationEvent[]
  helpAck: { byName: string; at: string } | null
  helpResolved: boolean
  acks: { incidentKey: string; ackedAt: string; ackedByName: string | null }[]
  missionsEnded: Set<number>
  /** Module Opérations : résultat de clôture, dispositions « non effectuée », motif d'annulation. */
  closeOutcome: 'COMPLETED' | 'WITH_EXCEPTIONS' | null
  notDone: Map<string, { reason: string; at: string }>
  cancelledAt: string | null
  cancelledReason: string | null
  /** Passages préparés (démonstration) : route → identifiant du passage 2. */
  preparedPasses: Map<string, string>
}

export interface FixtureOperationsCenterOptions {
  /** Vue sans opération : opérateurs connectés, aucune mission ni résidence. */
  withOperation?: boolean
  now?: () => number
}

export function createFixtureOperationsCenterRepository(
  options: FixtureOperationsCenterOptions = {},
): OperationsCenterRepository {
  const now = options.now ?? (() => Date.now())
  const iso = (minutesAgo: number) => new Date(now() - minutesAgo * 60_000).toISOString()
  const withOperation = options.withOperation ?? true
  let eventSeq = 0
  const state: State = {
    operationId: uuid(30, 14),
    version: 1,
    status: 'OPEN',
    openedAt: iso(263),
    closedAt: null,
    tasks: [],
    events: [],
    helpAck: null,
    helpResolved: false,
    acks: [],
    missionsEnded: new Set(),
    closeOutcome: null,
    notDone: new Map(),
    cancelledAt: null,
    cancelledReason: null,
    preparedPasses: new Map(),
  }

  function pushEvent(
    kind: string,
    payload: Record<string, unknown>,
    minutesAgo = 0,
    actorName: string | null = null,
  ) {
    eventSeq += 1
    state.events.push({
      id: eventSeq,
      kind,
      at: iso(minutesAgo),
      operationId: state.operationId,
      missionId: null,
      taskId: typeof payload.taskId === 'string' ? payload.taskId : null,
      actorName,
      payload,
    })
  }

  const missionId = (operator: DemoOperator) => uuid(40, operator.index)
  const employeeId = (operator: DemoOperator) => uuid(50, operator.index)

  // Résidences engagées : une tâche par adresse, couleur de la route, quelques déneigées, un problème par route.
  routes.forEach((route, routeIndex) => {
    const operator = demoOperators.find((candidate) => candidate.routeIndex === routeIndex)
    let order = 0
    const list: OperationTask[] = []
    for (const [street, x1, y1, x2, y2, count, start, step] of route.streets) {
      for (let k = 0; k < count; k += 1) {
        const f = count === 1 ? 0.5 : k / (count - 1)
        const point = P(x1 + (x2 - x1) * f, y1 + (y2 - y1) * f)
        order += 1
        const address = `${String(start + k * step)}, ${street}`
        list.push({
          id: uuid(60 + routeIndex, order),
          version: 1,
          status: 'TODO',
          passNumber: 1,
          serviceLocationId: uuid(70 + routeIndex, order),
          routeId: route.id,
          routeName: route.name,
          routeColor: route.color,
          routeOrder: order,
          snapshot: {
            addressLine1: address,
            addressLine2: null,
            city: 'Saint-Jérôme',
            postalCode: null,
            formattedAddress: address,
            clientName: clients[(order * 7 + routeIndex) % clients.length] ?? null,
            contractNumber: null,
            latitude: point.lat,
            longitude: point.lng,
            routeOrder: order,
            flag: null,
            captureRadiusM: 7,
          },
          ownerMissionId: operator ? missionId(operator) : null,
          ownerEmployeeId: operator ? employeeId(operator) : null,
          ownerName: operator?.name ?? null,
          ownerMissionOpen: true,
          needsReassignment: false,
          retryRequestedAt: null,
          inProgress: false,
          doneAt: null,
          resolutionReason: null,
          updatedAt: iso(route.startedMinutesAgo),
          attemptCount: 0,
          lastProblem: null,
          offer: null,
        })
      }
    }
    const done = Math.round(list.length * route.doneShare)
    list.slice(0, done).forEach((task, index) => {
      task.status = 'DONE'
      task.doneAt = iso(route.startedMinutesAgo - ((index + 1) * (route.startedMinutesAgo - 8)) / done)
      task.attemptCount = 1
    })
    if (route.problemIndex !== null && route.problem) {
      const task = list[route.problemIndex]
      if (task) {
        task.status = 'PROBLEM'
        task.attemptCount = 1
        task.lastProblem = {
          reasonCode: route.problem.reason,
          reasonText: null,
          note: route.problem.note,
          at: iso(route.problem.minutesAgo),
          operatorName: operator?.name ?? null,
        }
      }
    }
    const current = list[done]
    if (current?.status === 'TODO' && operator?.connection === 'online') current.inProgress = true
    state.tasks.push(...list)
  })

  pushEvent('OPERATION_OPENED', { number: '2026-014', operatorName: 'Marc Thibodeau' }, 263)
  for (const operator of demoOperators.filter((candidate) => candidate.routeIndex !== null)) {
    const route = routes[operator.routeIndex ?? 0]
    pushEvent(
      'MISSION_STARTED',
      {
        operatorName: operator.name,
        routeName: route?.name,
        equipmentLabel: operator.equipment,
        startEngineHours: operator.meter,
      },
      route?.startedMinutesAgo ?? 0,
    )
  }
  pushEvent('MISSION_ENDED', { operatorName: 'Patrick Roy', routeName: 'Route 5 · Centre-ville' }, 49)
  pushEvent('OPERATOR_AVAILABLE', { operatorName: 'Patrick Roy' }, 49)
  for (const task of state.tasks.filter((candidate) => candidate.status === 'PROBLEM')) {
    pushEvent(
      'TASK_PROBLEM',
      {
        taskId: task.id,
        address: task.snapshot.formattedAddress,
        reasonCode: task.lastProblem?.reasonCode,
        operatorName: task.ownerName,
      },
      Math.round((now() - Date.parse(task.lastProblem?.at ?? task.updatedAt)) / 60_000),
    )
  }
  pushEvent(
    'HELP_REQUESTED',
    { reason: 'DELAY', operatorName: 'Karine Ouellet', comment: 'Lame endommagée, avance lentement.' },
    6,
  )

  function operators(): CenterOperator[] {
    return demoOperators.map((operator) => {
      const route = operator.routeIndex === null ? null : routes[operator.routeIndex]
      const ownTasks = route ? state.tasks.filter((task) => task.routeId === route.id) : []
      const lastDone = ownTasks.filter((task) => task.status === 'DONE').at(-1)
      const anchor = ownTasks.find((task) => task.inProgress) ?? lastDone ?? ownTasks[0]
      const interrupted = operator.connection === 'interrupted'
      const heartbeatMinutes = interrupted ? 3.2 : 0.1
      const hasMission = route !== null && withOperation && !state.missionsEnded.has(operator.index)
      const position =
        operator.noPosition || (!anchor && route)
          ? null
          : {
              latitude: anchor?.snapshot.latitude ?? P(452, 602).lat,
              longitude: anchor?.snapshot.longitude ?? P(452, 602).lng,
              accuracy: 6,
              heading: 45,
              capturedAt: iso(interrupted ? 3.4 : 0.2),
              receivedAt: iso(interrupted ? 3.3 : 0.15),
              source: hasMission ? ('MISSION' as const) : ('HEARTBEAT' as const),
            }
      const total = ownTasks.length
      const completed = ownTasks.filter((task) => task.status === 'DONE').length
      const problem = ownTasks.filter((task) => task.status === 'PROBLEM').length
      return {
        employeeId: employeeId(operator),
        name: operator.name,
        assignedEquipmentLabel: operator.equipment,
        assignedRouteName: route?.name ?? null,
        presence: {
          connection: 'CONNECTED',
          connectedAt: iso(270),
          lastHeartbeatAt: iso(heartbeatMinutes),
          disconnectedAt: null,
          availableForHelp: operator.available,
          availableSince: operator.available ? iso(49) : null,
        },
        position,
        mission:
          hasMission && route
            ? {
                id: missionId(operator),
                kind: 'NORMAL',
                status: 'IN_PROGRESS',
                operationId: state.operationId,
                routeId: route.id,
                routeName: route.name,
                routeColor: route.color,
                equipmentLabel: operator.equipment,
                startedAt: iso(route.startedMinutesAgo),
                pausedAt: null,
                startEngineHours: operator.meter,
                progress: { total, completed, problem, waiting: total - completed - problem },
              }
            : null,
        lastClosedMission:
          operator.available && withOperation
            ? {
                id: uuid(41, operator.index),
                kind: 'NORMAL',
                status: 'COMPLETED',
                routeName: 'Route 5 · Centre-ville',
                endedAt: iso(49),
                cancelKind: null,
              }
            : null,
        openHelpRequest:
          operator.index === 4 && !state.helpResolved && withOperation
            ? {
                id: uuid(80, 1),
                reason: 'DELAY',
                status: state.helpAck ? 'ACKNOWLEDGED' : 'SENT',
                createdAt: iso(6),
              }
            : null,
      }
    })
  }

  function snapshot(): OperationsCenterSnapshot {
    const active = state.tasks.filter((task) => ['TODO', 'IN_PROGRESS', 'PROBLEM'].includes(task.status))
    const activeMissions = withOperation
      ? demoOperators.filter((op) => op.routeIndex !== null && !state.missionsEnded.has(op.index)).length
      : 0
    const counters = {
      activeMissions,
      pausedMissions: 0,
      total: state.tasks.length,
      remaining: active.length,
      problems: active.filter((task) => task.status === 'PROBLEM').length,
      done: state.tasks.filter((task) => task.status === 'DONE').length,
      resolvedNoService: state.tasks.filter((task) => task.status === 'RESOLVED_NO_SERVICE').length,
      toReassign: active.filter((task) => task.needsReassignment).length,
    }
    const activeOffers = state.tasks.filter((task) => task.offer?.status === 'ACTIVE').length
    const stateKind =
      state.status !== 'OPEN'
        ? state.status
        : activeMissions > 0
          ? 'ACTIVE'
          : counters.remaining === 0 && activeOffers === 0
            ? 'READY_TO_CLOSE'
            : 'MISSIONS_CLOSED_WITH_EXCEPTIONS'
    const base = {
      serverTime: new Date(now()).toISOString(),
      cursor: state.events.length,
      timezone: 'America/Toronto',
      operators: operators(),
      acks: state.acks,
    }
    if (!withOperation) {
      return {
        ...base,
        cursor: 0,
        operation: null,
        counters: null,
        routes: [],
        tasks: [],
        helpRequests: [],
        conflicts: [],
        acks: [],
        journal: [],
      }
    }
    return {
      ...base,
      operation: {
        id: state.operationId,
        number: '2026-014',
        season: '2026-2027',
        status: state.status,
        version: state.version,
        openedAt: state.openedAt,
        closedAt: state.closedAt,
        closedByName: state.closedAt ? 'Louis Martel' : null,
        closeNote: null,
        cancelledAt: null,
        reopenedAt: null,
        reopenedReason: null,
        stale: false,
        firstMissionAt: state.openedAt,
        lastMissionEndedAt: activeMissions > 0 ? null : iso(0),
        state: {
          status: state.status,
          activeMissions,
          unresolvedTasks: counters.remaining,
          problemTasks: counters.problems,
          activeOffers,
          openConflicts: 0,
          state: stateKind,
        },
      },
      counters,
      routes: routes.map((route) => {
        const list = state.tasks.filter((task) => task.routeId === route.id)
        const remaining = list.filter((task) => ['TODO', 'IN_PROGRESS', 'PROBLEM'].includes(task.status))
        const operator = demoOperators.find((candidate) => candidate.routeIndex === routes.indexOf(route))
        return {
          routeId: route.id,
          name: route.name,
          color: route.color,
          total: list.length,
          done: list.filter((task) => task.status === 'DONE').length,
          remaining: remaining.length,
          problems: remaining.filter((task) => task.status === 'PROBLEM').length,
          resolvedNoService: list.filter((task) => task.status === 'RESOLVED_NO_SERVICE').length,
          offered: remaining.filter((task) => task.offer?.status === 'ACTIVE').length,
          startedAt: iso(route.startedMinutesAgo),
          doneTimes: list.flatMap((task) => (task.status === 'DONE' && task.doneAt ? [task.doneAt] : [])),
          missions: operator
            ? [
                {
                  id: missionId(operator),
                  operatorName: operator.name,
                  status: 'IN_PROGRESS',
                  kind: 'NORMAL' as const,
                },
              ]
            : [],
        }
      }),
      tasks: active.map((task) => ({ ...task })),
      helpRequests: state.helpResolved
        ? []
        : [
            {
              id: uuid(80, 1),
              reason: 'DELAY',
              comment: 'Lame endommagée, avance lentement. Renfort souhaité pour la rue Bélanger.',
              status: state.helpAck ? 'ACKNOWLEDGED' : 'SENT',
              createdAt: iso(6),
              employeeId: uuid(50, 4),
              missionId: uuid(40, 4),
              operatorName: 'Karine Ouellet',
              acknowledgedAt: state.helpAck?.at ?? null,
              acknowledgedByName: state.helpAck?.byName ?? null,
              latitude: null,
              longitude: null,
            },
          ],
      conflicts: [],
      journal: [...state.events].reverse(),
    }
  }

  function findTask(taskId: string, expectedVersion?: number): OperationTask {
    const task = state.tasks.find((candidate) => candidate.id === taskId)
    if (!task) throw new OperationsCenterError('OPERATION_TASK_NOT_FOUND')
    if (expectedVersion !== undefined && task.version !== expectedVersion)
      throw new OperationsCenterError('OPERATION_TASK_VERSION_CONFLICT')
    if (state.status !== 'OPEN') throw new OperationsCenterError('OPERATION_NOT_OPEN')
    return task
  }

  function taskResult(task: OperationTask) {
    return {
      replayed: false,
      taskId: task.id,
      status: task.status,
      version: task.version,
      ownerMissionId: task.ownerMissionId,
      needsReassignment: task.needsReassignment,
      retryRequestedAt: task.retryRequestedAt,
      resolutionReason: task.resolutionReason,
    }
  }

  // ---------------------------------------------------------------- Module Opérations

  const unresolvedStatuses = new Set(['TODO', 'IN_PROGRESS', 'PROBLEM'])
  const isRemaining = (task: OperationTask) =>
    unresolvedStatuses.has(task.status) && !state.notDone.has(task.id)

  function moduleCounters(): OperationModuleCounters {
    const current = snapshot()
    const remaining = state.tasks.filter(isRemaining)
    return {
      activeMissions: current.counters?.activeMissions ?? 0,
      totalMissions: withOperation ? demoOperators.filter((op) => op.routeIndex !== null).length : 0,
      passes: state.tasks.length > 0 ? Math.max(...state.tasks.map((task) => task.passNumber)) : 0,
      uniqueResidences: new Set(state.tasks.map((task) => task.serviceLocationId)).size,
      expectedInterventions: state.tasks.length,
      done: state.tasks.filter((task) => task.status === 'DONE').length,
      resolvedNoService: state.tasks.filter((task) => task.status === 'RESOLVED_NO_SERVICE').length,
      remaining: remaining.length,
      problems: remaining.filter((task) => task.status === 'PROBLEM').length,
      notDoneAtClose: state.notDone.size,
      notDoneProblems: state.tasks.filter((task) => state.notDone.has(task.id) && task.status === 'PROBLEM')
        .length,
      retries: 0,
      activeOffers: state.tasks.filter((task) => task.offer?.status === 'ACTIVE').length,
      openConflicts: 0,
    }
  }

  function moduleRow(): OperationModuleRow {
    const current = snapshot()
    return {
      id: state.operationId,
      number: '2026-014',
      season: '2026-2027',
      status: state.status,
      version: state.version,
      state: current.operation?.state.state ?? 'ACTIVE',
      openedAt: state.openedAt,
      firstMissionAt: state.openedAt,
      serviceEndedAt: current.operation?.lastMissionEndedAt ?? null,
      closedAt: state.closedAt,
      cancelledAt: state.cancelledAt,
      closeOutcome: state.status === 'CLOSED' ? (state.closeOutcome ?? 'COMPLETED') : null,
      stale: false,
      counters: moduleCounters(),
    }
  }

  function moduleDetail(): OperationDetail {
    const current = snapshot()
    return {
      ...moduleRow(),
      serverTime: new Date(now()).toISOString(),
      timezone: 'America/Toronto',
      stateDetail: current.operation?.state ?? {
        status: state.status,
        activeMissions: 0,
        unresolvedTasks: 0,
        problemTasks: 0,
        activeOffers: 0,
        state: 'ACTIVE',
      },
      closedByName: state.closedAt ? 'Louis Martel' : null,
      closeNote: null,
      cancelledByName: state.cancelledAt ? 'Louis Martel' : null,
      cancelledReason: state.cancelledReason,
      reopenedAt: null,
      reopenedByName: null,
      reopenedReason: null,
      missions: demoOperators
        .filter((operator) => operator.routeIndex !== null)
        .map((operator) => {
          const route = routes[operator.routeIndex ?? 0]
          const ended = state.missionsEnded.has(operator.index)
          return {
            id: missionId(operator),
            kind: 'NORMAL' as const,
            status: ended ? 'COMPLETED' : 'IN_PROGRESS',
            cancelKind: null,
            routeId: route?.id ?? null,
            routeName: route?.name ?? null,
            operatorName: operator.name,
            equipmentLabel: operator.equipment,
            startedAt: iso(route?.startedMinutesAgo ?? 0),
            endedAt: ended ? iso(0) : null,
            doneTasks: state.tasks.filter((task) => task.routeId === route?.id && task.status === 'DONE')
              .length,
            routes: route ? [route.id] : [],
          }
        }),
      routes: routes.map((route) => {
        const list = state.tasks.filter((task) => task.routeId === route.id)
        const remaining = list.filter(isRemaining)
        return {
          routeId: route.id,
          name: route.name,
          color: route.color,
          passes: [
            {
              passNumber: 1,
              status: 'STARTED' as const,
              total: list.length,
              done: list.filter((task) => task.status === 'DONE').length,
              remaining: remaining.length,
              problems: remaining.filter((task) => task.status === 'PROBLEM').length,
              resolvedNoService: list.filter((task) => task.status === 'RESOLVED_NO_SERVICE').length,
              notDoneAtClose: list.filter((task) => state.notDone.has(task.id)).length,
            },
            ...(state.preparedPasses.has(route.id)
              ? [
                  {
                    passNumber: 2,
                    passId: state.preparedPasses.get(route.id) ?? null,
                    status: 'PREPARED' as const,
                    preparedAt: iso(0),
                    total: 0,
                    done: 0,
                    remaining: 0,
                    problems: 0,
                    resolvedNoService: 0,
                    notDoneAtClose: 0,
                  },
                ]
              : []),
          ],
        }
      }),
    }
  }

  function fingerprint(): string {
    return [
      state.version,
      ...state.tasks.map((task) => `${task.id}:${String(task.version)}:${task.offer?.status ?? ''}`),
      ...state.missionsEnded,
    ].join('|')
  }

  function diagnostic(): CloseDiagnostic {
    const current = snapshot()
    const open = state.status === 'OPEN'
    const openMissions = demoOperators.filter(
      (operator) => withOperation && operator.routeIndex !== null && !state.missionsEnded.has(operator.index),
    )
    const remaining = state.tasks.filter(isRemaining)
    const problems = remaining.filter((task) => task.status === 'PROBLEM').length
    const offers = state.tasks.filter((task) => task.offer?.status === 'ACTIVE')
    const help = current.helpRequests
    const hasWork = state.tasks.some((task) => task.status !== 'TODO')
    const blocked = (condition: boolean) => (condition ? 'BLOCKER' : 'OK')
    return {
      operationId: state.operationId,
      number: '2026-014',
      status: state.status,
      version: state.version,
      fingerprint: fingerprint(),
      readAt: new Date(now()).toISOString(),
      state: current.operation?.state ?? {
        status: state.status,
        activeMissions: 0,
        unresolvedTasks: 0,
        problemTasks: 0,
        activeOffers: 0,
        state: 'ACTIVE',
      },
      hasWork,
      categories: [
        {
          key: 'OPEN_MISSIONS',
          count: openMissions.length,
          severity: blocked(openMissions.length > 0),
          items: openMissions.map((operator) => ({
            missionId: missionId(operator),
            kind: 'NORMAL' as const,
            status: 'IN_PROGRESS',
            operatorName: operator.name,
            equipmentLabel: operator.equipment,
            routeName: routes[operator.routeIndex ?? 0]?.name ?? null,
            startedAt: iso(routes[operator.routeIndex ?? 0]?.startedMinutesAgo ?? 0),
            pausedAt: null,
          })),
        },
        {
          key: 'UNRESOLVED_TASKS',
          count: remaining.length,
          problems,
          truncated: remaining.length > 100,
          severity: blocked(remaining.length > 0),
          items: remaining.slice(0, 100).map((task) => ({
            taskId: task.id,
            status: task.status,
            passNumber: task.passNumber,
            routeId: task.routeId,
            routeName: task.routeName,
            routeColor: task.routeColor,
            address: task.snapshot.formattedAddress ?? null,
            needsReassignment: task.needsReassignment,
            ownerName: task.ownerName,
          })),
        },
        {
          key: 'ACTIVE_OFFERS',
          count: offers.length,
          severity: blocked(offers.length > 0),
          items: offers.map((task) => ({
            offerId: task.offer?.id ?? task.id,
            taskId: task.id,
            createdAt: task.offer?.createdAt ?? iso(0),
            address: task.snapshot.formattedAddress ?? null,
            routeName: task.routeName,
          })),
        },
        { key: 'OPEN_CONFLICTS', count: 0, severity: 'OK', items: [] },
        {
          key: 'HELP_REQUESTS',
          count: help.length,
          severity: help.length > 0 ? 'WARNING' : 'OK',
          items: help.map((request) => ({
            helpRequestId: request.id,
            reason: request.reason,
            status: request.status,
            createdAt: request.createdAt,
            missionId: request.missionId,
            operatorName: request.operatorName,
          })),
        },
      ],
      outcomes: {
        close: {
          available: open && openMissions.length === 0 && remaining.length === 0 && offers.length === 0,
          reason: !open
            ? 'OPERATION_NOT_OPEN'
            : openMissions.length > 0
              ? 'OPERATION_HAS_OPEN_MISSIONS'
              : remaining.length > 0
                ? 'OPERATION_HAS_UNRESOLVED_TASKS'
                : offers.length > 0
                  ? 'OPERATION_OFFERS_ACTIVE'
                  : null,
        },
        closeWithExceptions: {
          available: open && openMissions.length === 0 && (remaining.length > 0 || offers.length > 0),
          reason: !open
            ? 'OPERATION_NOT_OPEN'
            : openMissions.length > 0
              ? 'OPERATION_HAS_OPEN_MISSIONS'
              : remaining.length === 0 && offers.length === 0
                ? 'OPERATION_NO_EXCEPTIONS'
                : null,
          notDone: remaining.length,
          problems,
          offersToRevoke: offers.length,
        },
        cancel: {
          available: open && openMissions.length === 0 && !hasWork,
          reason: !open
            ? 'OPERATION_NOT_OPEN'
            : openMissions.length > 0
              ? 'OPERATION_HAS_OPEN_MISSIONS'
              : hasWork
                ? 'OPERATION_HAS_WORK'
                : null,
          offersToRevoke: offers.length,
        },
      },
    }
  }

  function assertUnchanged(command: { expectedVersion: number; expectedFingerprint: string }) {
    if (state.status !== 'OPEN') throw new OperationsCenterError('OPERATION_NOT_OPEN')
    if (command.expectedVersion !== state.version)
      throw new OperationsCenterError('OPERATION_VERSION_CONFLICT')
    if (command.expectedFingerprint !== fingerprint()) throw new OperationsCenterError('OPERATION_CHANGED')
  }

  function revokeOffers(): number {
    let count = 0
    for (const task of state.tasks) {
      if (task.offer?.status === 'ACTIVE') {
        task.offer = { ...task.offer, status: 'REVOKED' }
        task.version += 1
        count += 1
      }
    }
    return count
  }

  const wait = <T>(value: T) =>
    new Promise<T>((resolve) => {
      setTimeout(() => {
        resolve(value)
      }, 60)
    })

  return {
    snapshot: () => wait(snapshot()),
    changes: (since) =>
      wait({
        serverTime: new Date(now()).toISOString(),
        resync: false,
        cursor: state.events.length,
        events: state.events.filter((event) => event.id > since),
        operationId: withOperation ? state.operationId : null,
        operators: operators(),
      }),
    tracks: (operationId, missionIds) => {
      const result: OperationTracks = {
        operationId,
        serverTime: new Date(now()).toISOString(),
        missions: demoOperators
          .filter((operator) => operator.routeIndex !== null)
          .filter((operator) => !missionIds || missionIds.includes(missionId(operator)))
          .map((operator) => {
            const route = routes[operator.routeIndex ?? 0]
            const list = state.tasks.filter((task) => task.routeId === route?.id && task.status === 'DONE')
            const startMs = now() - (route?.startedMinutesAgo ?? 0) * 60_000
            // Relevés toutes les 30 s le long des résidences déneigées; une coupure de collecte pour la tablette T-11.
            const points = list.flatMap((task, index) => {
              if (operator.connection === 'interrupted' && index >= 4 && index < 6) return []
              return [
                {
                  id: uuid(90 + operator.index, index + 1),
                  capturedAt: new Date(startMs + index * 30_000).toISOString(),
                  receivedAt: new Date(startMs + index * 30_000 + 800).toISOString(),
                  latitude: task.snapshot.latitude ?? 0,
                  longitude: task.snapshot.longitude ?? 0,
                  accuracy: 6,
                },
              ]
            })
            return {
              missionId: missionId(operator),
              employeeId: employeeId(operator),
              equipmentId: uuid(20, operator.index),
              equipmentLabel: operator.equipment,
              operatorName: operator.name,
              kind: 'NORMAL' as const,
              status: 'IN_PROGRESS',
              startedAt: new Date(startMs).toISOString(),
              endedAt: null,
              points,
              pauses: [],
              gaps:
                operator.connection === 'interrupted'
                  ? [
                      {
                        kind: 'POSITION_UNAVAILABLE' as const,
                        startedAt: new Date(startMs + 4 * 30_000 - 1000).toISOString(),
                        endedAt: new Date(startMs + 6 * 30_000 - 1000).toISOString(),
                      },
                    ]
                  : [],
            }
          }),
      }
      return wait(result)
    },
    stats: (operationId) => {
      const result: OperationStats = {
        operationId,
        serverTime: new Date(now()).toISOString(),
        firstMissionAt: state.openedAt,
        lastMissionEndedAt: null,
        closedAt: state.closedAt,
        elapsedSeconds: Math.round((now() - Date.parse(state.openedAt)) / 1000),
        activeMissions: 4,
        tasks: snapshot().counters ?? {
          activeMissions: 0,
          pausedMissions: 0,
          total: 0,
          remaining: 0,
          problems: 0,
          done: 0,
          resolvedNoService: 0,
          toReassign: 0,
        },
        routes: snapshot().routes.map((route) => ({
          routeId: route.routeId,
          name: route.name,
          color: route.color,
          total: route.total,
          done: route.done,
          remaining: route.remaining,
          problems: route.problems,
          resolvedNoService: route.resolvedNoService,
          doneByReinforcement: 0,
          retries: 0,
        })),
        missions: demoOperators
          .filter((operator) => operator.routeIndex !== null)
          .map((operator) => {
            const route = routes[operator.routeIndex ?? 0]
            return {
              missionId: missionId(operator),
              employeeId: employeeId(operator),
              operatorName: operator.name,
              equipmentId: uuid(20, operator.index),
              equipmentLabel: operator.equipment,
              kind: 'NORMAL' as const,
              routeName: route?.name ?? null,
              status: 'IN_PROGRESS',
              cancelKind: null,
              startedAt: iso(route?.startedMinutesAgo ?? 0),
              endedAt: null,
              pausedSeconds: operator.index === 2 ? 720 : 0,
              pauseCount: operator.index === 2 ? 1 : 0,
              startEngineHours: operator.meter,
              endEngineHours: null,
              done: state.tasks.filter((task) => task.routeId === route?.id && task.status === 'DONE').length,
              doneOtherRoutes: 0,
              problemsReported: route?.problemIndex === null ? 0 : 1,
              reinforcementReceived: 0,
              helpRequests: operator.index === 4 ? 1 : 0,
              gpsPoints: 40,
              gpsGaps: operator.connection === 'interrupted' ? 1 : 0,
              lastPositionReceivedAt: iso(operator.connection === 'interrupted' ? 3.3 : 0.15),
            }
          }),
        helpRequests: 1,
        offersPublished: state.tasks.filter((task) => task.offer).length,
        offersClaimed: 0,
      }
      return wait(result)
    },
    taskDetail: (taskId) => {
      const task = findTask(taskId)
      return wait({
        ...task,
        operationId: state.operationId,
        clientContractId: null,
        attempts: task.lastProblem
          ? [
              {
                id: uuid(85, task.routeOrder ?? 1),
                result: 'PROBLEM' as const,
                reasonCode: task.lastProblem.reasonCode,
                reasonText: null,
                note: task.lastProblem.note,
                source: 'MANUAL',
                at: task.lastProblem.at,
                missionId: task.ownerMissionId,
                operatorName: task.ownerName,
                actorName: task.ownerName,
              },
            ]
          : [],
        conflicts: [],
      })
    },
    tasks: (_operationId, status, search) =>
      wait(
        state.tasks.filter(
          (task) =>
            task.status === status &&
            (!search || (task.snapshot.formattedAddress ?? '').toLowerCase().includes(search.toLowerCase())),
        ),
      ),
    operations: () =>
      wait({
        page: 1,
        pageSize: 20,
        total: withOperation ? 1 : 0,
        rows: withOperation
          ? [
              {
                id: state.operationId,
                number: '2026-014',
                season: '2026-2027',
                status: state.status,
                openedAt: state.openedAt,
                closedAt: state.closedAt,
                cancelledAt: null,
                missions: 4,
                counters: snapshot().counters ?? {
                  activeMissions: 0,
                  pausedMissions: 0,
                  total: 0,
                  remaining: 0,
                  problems: 0,
                  done: 0,
                  resolvedNoService: 0,
                  toReassign: 0,
                },
              },
            ]
          : [],
      }),
    closeOperation: (command) => {
      if (command.expectedVersion !== state.version)
        throw new OperationsCenterError('OPERATION_VERSION_CONFLICT')
      if (snapshot().operation?.state.state !== 'READY_TO_CLOSE')
        throw new OperationsCenterError('OPERATION_NOT_READY')
      state.status = 'CLOSED'
      state.closedAt = iso(0)
      state.version += 1
      pushEvent('OPERATION_CLOSED', { number: '2026-014' }, 0, 'Louis Martel')
      return wait({
        replayed: false,
        operationId: state.operationId,
        status: 'CLOSED' as const,
        version: state.version,
        closedAt: state.closedAt,
      })
    },
    reopenOperation: (command) => {
      if (state.status !== 'CLOSED') throw new OperationsCenterError('OPERATION_NOT_CLOSED')
      if (command.expectedVersion !== state.version)
        throw new OperationsCenterError('OPERATION_VERSION_CONFLICT')
      state.status = 'OPEN'
      state.closedAt = null
      state.closeOutcome = null
      state.notDone.clear()
      state.version += 1
      pushEvent('OPERATION_REOPENED', { reason: command.reason }, 0, 'Louis Martel')
      return wait({
        replayed: false,
        operationId: state.operationId,
        status: 'OPEN' as const,
        version: state.version,
      })
    },
    cancelMission: () => wait(undefined),
    retryTask: (command) => {
      const task = findTask(command.taskId, command.expectedVersion)
      if (task.status !== 'PROBLEM') throw new OperationsCenterError('OPERATION_TASK_NOT_PROBLEM')
      task.retryRequestedAt = iso(0)
      task.version += 1
      pushEvent(
        'TASK_RETRY_REQUESTED',
        { taskId: task.id, address: task.snapshot.formattedAddress, routeName: task.routeName },
        0,
        'Louis Martel',
      )
      return wait(taskResult(task))
    },
    resolveTask: (command) => {
      const task = findTask(command.taskId, command.expectedVersion)
      task.status = 'RESOLVED_NO_SERVICE'
      task.resolutionReason = command.reason
      task.version += 1
      pushEvent(
        'TASK_RESOLVED_NO_SERVICE',
        { taskId: task.id, address: task.snapshot.formattedAddress, reason: command.reason },
        0,
        'Louis Martel',
      )
      return wait(taskResult(task))
    },
    resetTask: (command) => {
      const task = findTask(command.taskId, command.expectedVersion)
      if (task.status !== 'DONE') throw new OperationsCenterError('OPERATION_TASK_NOT_DONE')
      task.status = 'TODO'
      task.doneAt = null
      task.version += 1
      pushEvent(
        'TASK_RESET_TODO',
        { taskId: task.id, address: task.snapshot.formattedAddress, reason: command.reason },
        0,
        'Louis Martel',
      )
      return wait(taskResult(task))
    },
    reassignTask: (command) => {
      const task = findTask(command.taskId, command.expectedVersion)
      const operator = demoOperators.find((candidate) => employeeId(candidate) === command.toEmployeeId)
      if (operator?.routeIndex === undefined || operator.routeIndex === null)
        throw new OperationsCenterError('OPERATION_TARGET_WITHOUT_MISSION')
      task.ownerMissionId = missionId(operator)
      task.ownerName = operator.name
      task.version += 1
      pushEvent(
        'TASK_REASSIGNED',
        { taskId: task.id, address: task.snapshot.formattedAddress, operatorName: operator.name },
        0,
        'Louis Martel',
      )
      return wait({ ...taskResult(task), itemId: uuid(95, 1) })
    },
    publishOffer: (command) => {
      const ids = [...new Set([...command.taskIds, ...command.retryTaskIds])]
      const offers = ids.map((taskId, index) => {
        const task = findTask(taskId)
        if (task.offer?.status === 'ACTIVE' || task.inProgress)
          throw new OperationsCenterError('OFFER_TASK_NOT_ELIGIBLE')
        task.offer = {
          id: uuid(96, state.events.length * 100 + index),
          status: 'ACTIVE',
          isRetry: task.status === 'PROBLEM',
          createdAt: iso(0),
          claimedAt: null,
          claimedByName: null,
        }
        task.version += 1
        return { offerId: task.offer.id, taskId }
      })
      pushEvent(
        'OFFER_PUBLISHED',
        { tasks: offers.length, allEligible: command.recipients === 'ALL' },
        0,
        'Louis Martel',
      )
      return wait({
        replayed: false,
        operationId: command.operationId,
        offers,
        allEligible: command.recipients === 'ALL',
        recipients: command.recipients === 'ALL' ? [] : command.recipients,
      })
    },
    revokeOffer: (command) => {
      const task = state.tasks.find((candidate) => candidate.offer?.id === command.offerId)
      if (task?.offer?.status === 'CLAIMED') throw new OperationsCenterError('OFFER_ALREADY_CLAIMED')
      if (task?.offer) task.offer = { ...task.offer, status: 'REVOKED' }
      return wait(undefined)
    },
    acknowledgeHelp: () => {
      state.helpAck ??= { byName: 'Louis Martel (vous)', at: iso(0) }
      pushEvent('HELP_ACKNOWLEDGED', { reason: 'DELAY' }, 0, 'Louis Martel')
      return wait(undefined)
    },
    resolveHelp: () => {
      state.helpResolved = true
      pushEvent('HELP_RESOLVED', { reason: 'DELAY' }, 0, 'Louis Martel')
      return wait(undefined)
    },
    acknowledgeWatch: (incidentKey) => {
      if (!state.acks.some((ack) => ack.incidentKey === incidentKey))
        state.acks.push({ incidentKey, ackedAt: iso(0), ackedByName: 'Louis Martel (vous)' })
      return wait(undefined)
    },
    resolveConflict: () => wait(undefined),
    operationsModule: (query) => {
      const row = moduleRow()
      const views = {
        ALL: 1,
        ACTIVE: row.state === 'ACTIVE' ? 1 : 0,
        TO_CLOSE: row.state === 'READY_TO_CLOSE' || row.state === 'MISSIONS_CLOSED_WITH_EXCEPTIONS' ? 1 : 0,
        WITH_EXCEPTIONS:
          row.state === 'MISSIONS_CLOSED_WITH_EXCEPTIONS' || row.closeOutcome === 'WITH_EXCEPTIONS' ? 1 : 0,
        CLOSED: row.status === 'CLOSED' ? 1 : 0,
        CANCELLED: row.status === 'CANCELLED' ? 1 : 0,
      }
      const matches =
        withOperation &&
        views[query.view] === 1 &&
        (!query.search || row.number.toLowerCase().includes(query.search.toLowerCase())) &&
        (!query.season || query.season === row.season)
      return wait({
        page: query.page,
        pageSize: query.pageSize,
        timezone: 'America/Toronto',
        total: matches ? 1 : 0,
        views: withOperation
          ? views
          : { ALL: 0, ACTIVE: 0, TO_CLOSE: 0, WITH_EXCEPTIONS: 0, CLOSED: 0, CANCELLED: 0 },
        seasons: withOperation ? ['2026-2027'] : [],
        rows: matches && query.page === 1 ? [row] : [],
      })
    },
    operationDetail: (operationId) => {
      if (!withOperation || operationId !== state.operationId)
        throw new OperationsCenterError('OPERATION_NOT_FOUND')
      return wait(moduleDetail())
    },
    closeDiagnostic: (operationId) => {
      if (!withOperation || operationId !== state.operationId)
        throw new OperationsCenterError('OPERATION_NOT_FOUND')
      return wait(diagnostic())
    },
    closeWithExceptions: (command) => {
      assertUnchanged(command)
      const current = diagnostic()
      if (!current.outcomes.closeWithExceptions.available)
        throw new OperationsCenterError(current.outcomes.closeWithExceptions.reason ?? 'OPERATION_NOT_READY')
      if (current.outcomes.closeWithExceptions.offersToRevoke > 0 && !command.revokeOffers)
        throw new OperationsCenterError('OPERATION_OFFERS_ACTIVE')
      const offersRevoked = revokeOffers()
      const remaining = state.tasks.filter(isRemaining)
      for (const task of remaining) state.notDone.set(task.id, { reason: command.reason, at: iso(0) })
      state.status = 'CLOSED'
      state.closedAt = iso(0)
      state.closeOutcome = 'WITH_EXCEPTIONS'
      state.version += 1
      pushEvent(
        'OPERATION_CLOSED',
        { number: '2026-014', outcome: 'WITH_EXCEPTIONS', reason: command.reason, notDone: remaining.length },
        0,
        'Louis Martel',
      )
      return wait({
        replayed: false,
        operationId: state.operationId,
        status: 'CLOSED' as const,
        version: state.version,
        closedAt: state.closedAt,
        closeOutcome: 'WITH_EXCEPTIONS' as const,
        notDone: remaining.length,
        problems: remaining.filter((task) => task.status === 'PROBLEM').length,
        offersRevoked,
      })
    },
    cancelOperation: (command) => {
      assertUnchanged(command)
      const current = diagnostic()
      if (!current.outcomes.cancel.available)
        throw new OperationsCenterError(current.outcomes.cancel.reason ?? 'OPERATION_NOT_CANCELLABLE')
      if (current.outcomes.cancel.offersToRevoke > 0 && !command.revokeOffers)
        throw new OperationsCenterError('OPERATION_OFFERS_ACTIVE')
      const offersRevoked = revokeOffers()
      const withdrawnTasks = state.tasks.length
      state.tasks = []
      state.status = 'CANCELLED'
      state.cancelledAt = iso(0)
      state.cancelledReason = command.reason
      state.version += 1
      pushEvent('OPERATION_CANCELLED', { reason: 'ADMIN', note: command.reason }, 0, 'Louis Martel')
      return wait({
        replayed: false,
        operationId: state.operationId,
        status: 'CANCELLED' as const,
        version: state.version,
        cancelledAt: state.cancelledAt,
        withdrawnTasks,
        offersRevoked,
      })
    },
    residences: (query) => {
      const matches = state.tasks.filter((task) => {
        if (query.routeId && task.routeId !== query.routeId) return false
        if (query.passNumber && task.passNumber !== query.passNumber) return false
        if (
          query.search &&
          !(task.snapshot.formattedAddress ?? '').toLowerCase().includes(query.search.toLowerCase())
        )
          return false
        switch (query.filter) {
          case 'REMAINING':
            return isRemaining(task)
          case 'PROBLEM':
            return isRemaining(task) && task.status === 'PROBLEM'
          case 'DONE':
            return task.status === 'DONE'
          case 'RESOLVED_NO_SERVICE':
            return task.status === 'RESOLVED_NO_SERVICE'
          case 'NOT_DONE_AT_CLOSE':
            return state.notDone.has(task.id)
          case 'ALL':
            return true
        }
      })
      const start = (query.page - 1) * query.pageSize
      return wait({
        page: query.page,
        pageSize: query.pageSize,
        total: matches.length,
        rows: matches.slice(start, start + query.pageSize).map((task) => {
          const disposition = state.notDone.get(task.id)
          return {
            ...task,
            closureDisposition: disposition ? ('NOT_DONE_AT_CLOSE' as const) : null,
            closureReason: disposition?.reason ?? null,
            closureAt: disposition?.at ?? null,
            closureByName: disposition ? 'Louis Martel' : null,
            doneByName: task.status === 'DONE' ? task.ownerName : null,
          }
        }),
      })
    },
    routePasses: () => {
      const open = state.status === 'OPEN'
      return wait({
        operationId: state.operationId,
        version: state.version,
        readAt: new Date(now()).toISOString(),
        routes: routes.map((route) => {
          const list = state.tasks.filter((task) => task.routeId === route.id)
          const remaining = list.filter(isRemaining).length
          const missionOpen = demoOperators.some(
            (operator) =>
              operator.routeIndex === routes.indexOf(route) && !state.missionsEnded.has(operator.index),
          )
          const prepared = state.preparedPasses.get(route.id) ?? null
          const action = missionOpen
            ? ('IN_USE' as const)
            : prepared
              ? ('NEW_PASS' as const)
              : remaining > 0
                ? ('RESUME' as const)
                : ('COMPLETED' as const)
          return {
            routeId: route.id,
            name: route.name,
            color: route.color,
            eligibleNow: list.length,
            action,
            passNumber: prepared ? 2 : 1,
            currentPass: 1,
            preparedPassId: prepared,
            total: list.length,
            done: list.filter((task) => task.status === 'DONE').length,
            remaining,
            inUseBy: missionOpen
              ? (demoOperators.find((operator) => operator.routeIndex === routes.indexOf(route))?.name ??
                null)
              : null,
            canPrepare: open && action === 'COMPLETED',
            reason: !open
              ? 'OPERATION_NOT_OPEN'
              : action === 'IN_USE'
                ? 'ROUTE_PASS_MISSION_OPEN'
                : action === 'NEW_PASS'
                  ? 'ROUTE_PASS_ALREADY_PREPARED'
                  : action === 'RESUME'
                    ? 'ROUTE_PASS_PREVIOUS_UNFINISHED'
                    : null,
          }
        }),
      })
    },
    preparePasses: (command) => {
      const passes = command.routeIds.map((routeId, index) => {
        if (state.preparedPasses.has(routeId)) throw new OperationsCenterError('ROUTE_PASS_ALREADY_PREPARED')
        const passId = uuid(90, state.preparedPasses.size + index + 1)
        return { passId, routeId, passNumber: 2, eligibleResidences: 0 }
      })
      for (const pass of passes) state.preparedPasses.set(pass.routeId, pass.passId)
      pushEvent('ROUTE_PASS_PREPARED', { passNumber: 2 }, 0, 'Louis Martel')
      return wait({ replayed: false, operationId: state.operationId, passes })
    },
    cancelPass: (command) => {
      for (const [routeId, passId] of state.preparedPasses)
        if (passId === command.passId) state.preparedPasses.delete(routeId)
      pushEvent('ROUTE_PASS_CANCELLED', { reason: command.reason }, 0, 'Louis Martel')
      return wait(undefined)
    },
    journal: (_operationId, beforeId) => {
      const ordered = [...state.events].reverse().filter((event) => !beforeId || event.id < beforeId)
      const page = ordered.slice(0, 50)
      const last = page.at(-1)
      return wait({ events: page, nextBeforeId: ordered.length > 50 && last ? last.id : null })
    },
  }
}
